/** Same account preference and GraphQL mutation used by sgapp-client /preview. */
export const notificationApiUrl = (hostname: string) => ['labs.dev.240.org', 'preview.dev.240.org'].includes(hostname)
  ? 'https://api.240tutoring.com/sgapp-dev-crq'
  : 'https://api.240tutoring.com/sgapp/v2';

export type GetToken = () => Promise<string | null>;
interface OptIn { programId: string; notify: boolean }

/**
 * `programId` must be on the study app's server-side allowlist (sgapp-server,
 * `LABS_PROGRAM_IDS`), or the mutation answers "Invalid labs program". Adding a program
 * here means adding it there too: project-180, marketplace, and preview-updates.
 */
export async function programNotification(
  programId: string,
  getToken: GetToken,
  signal: AbortSignal,
  notify?: boolean,
): Promise<boolean> {
  const token = await getToken();
  if (!token) throw new Error('Sign in to save your notification preference.');
  // login resolves the current study-app user from the token, as in the study app's
  // startup. Clerk IDs are not study-app user IDs and must not be used in user(id:).
  const query = notify === undefined
    ? 'mutation PreviewNotificationStatus { login { labsOptIns { programId notify } } }'
    : `mutation UserSetLabsOptIn($programId: String!, $notify: Boolean) {
        userSetLabsOptIn(programId: $programId, notify: $notify) {
          labsOptIns { programId notify }
        }
      }`;
  const response = await fetch(notificationApiUrl(window.location.hostname), {
    method: 'POST',
    signal,
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ query, variables: notify === undefined ? {} : { programId, notify } }),
  });
  if (!response.ok) throw new Error('Notification request failed.');
  const body = await response.json() as {
    errors?: unknown[];
    data?: { login?: { labsOptIns: OptIn[] }; userSetLabsOptIn?: { labsOptIns: OptIn[] } };
  };
  const user = notify === undefined ? body.data?.login : body.data?.userSetLabsOptIn;
  if (body.errors?.length || !Array.isArray(user?.labsOptIns)) throw new Error('Notification request failed.');
  return user.labsOptIns.some(item => item.programId === programId && item.notify === true);
}

export const project180Notification = (getToken: GetToken, signal: AbortSignal, notify?: boolean) =>
  programNotification('project-180', getToken, signal, notify);
